import React from 'react';
import {useCart} from '@shopify/hydrogen-react';
import styled from 'styled-components';

const CartBody = styled.div`
  padding: 12px;
  display: grid;
  gap: 10px;
`;

const CartItem = styled.div`
  border: 1px solid #dce3df;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
  display: grid;
  gap: 8px;
`;

const CartTitleRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
`;

const CartTitle = styled.h4`
  margin: 0;
  color: #17251d;
  font-size: 0.96rem;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const CartVariant = styled.div`
  color: #667085;
  font-size: 0.82rem;
  font-weight: 700;
  margin-top: 2px;
`;

const RemoveButton = styled.button`
  border: 1px solid #e0b4a9;
  border-radius: 8px;
  background: #fff7f5;
  color: #8a321f;
  min-height: 32px;
  padding: 0 10px;
  font-size: 0.8rem;
  font-weight: 850;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #ffe9e3;
  }
`;

const CartMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  color: #344054;
  font-size: 0.84rem;
  font-weight: 750;
`;

const MetaPill = styled.span`
  border: 1px solid #d5ddd9;
  border-radius: 999px;
  background: #f7f9f8;
  padding: 4px 8px;
`;

const RequestList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RequestPill = styled.span`
  border: 1px solid #c8ddd5;
  border-radius: 999px;
  background: #eef8f4;
  color: #1b6a59;
  padding: 4px 8px;
  font-size: 0.8rem;
  font-weight: 800;
  max-width: 100%;
  overflow-wrap: anywhere;
`;

const CartTotal = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid #e4e9e6;
  padding: 12px 2px 2px;
  color: #17251d;
  font-weight: 900;
`;

const formatMoney = (amount, currencyCode) => {
  const numericAmount = Number(amount || 0);
  if (!Number.isFinite(numericAmount)) {
    return `${amount || '0.00'} ${currencyCode || ''}`.trim();
  }

  return `${numericAmount.toFixed(2)} ${currencyCode || ''}`.trim();
};

const getAttributes = (line) => line.attributes || [];

const isChildLine = (line) => getAttributes(line).some(attribute => attribute.key === '_io_parent_order_group');

const getOrderGroup = (line) => {
  return getAttributes(line).find(attribute => attribute.key === '_io_order_group')?.value || null;
};

const getParentOrderGroup = (line) => {
  return getAttributes(line).find(attribute => attribute.key === '_io_parent_order_group')?.value || null;
};

const getLineTitle = (line) => {
  const productTitle = line.merchandise?.product?.title || '';
  const variantTitle = line.merchandise?.title || '';
  if (!productTitle) return variantTitle || 'Item';
  return productTitle;
};

const getVariantTitle = (line) => {
  const variantTitle = line.merchandise?.title || '';
  return variantTitle === 'Default Title' ? '' : variantTitle;
};

const CartDetails = ({ lines, cost }) => {
  const cart = useCart();
  const { linesRemove } = cart;

  const handleRemove = (lineId) => {
    const lineToRemove = lines.find(line => line.id === lineId);
    if (!lineToRemove) {
      return;
    }

    const orderGroupValue = getOrderGroup(lineToRemove);
    let relatedLineIds = [lineId];

    if (orderGroupValue) {
      const relatedLines = lines.filter(line => getParentOrderGroup(line) === orderGroupValue);
      relatedLineIds = [...relatedLineIds, ...relatedLines.map(line => line.id)];
    }

    linesRemove(relatedLineIds);
  };

  React.useEffect(() => {
    console.log('CartDetails - cost changed:', cost);
  }, [cost]);

  const parentLines = lines.filter(line => !isChildLine(line));

  return (
    <CartBody>
      {parentLines.map((line, index) => {
        const orderGroup = getOrderGroup(line);
        const relatedLines = orderGroup
          ? lines.filter(childLine => getParentOrderGroup(childLine) === orderGroup)
          : [];
        const selectedRequestNames = getAttributes(line)
          .filter(attribute => attribute.key && !attribute.key.startsWith('_io_'))
          .map(attribute => attribute.key);
        const relatedRequestNames = relatedLines.map(relatedLine => {
          const variantTitle = getVariantTitle(relatedLine);
          return variantTitle ? `${getLineTitle(relatedLine)}: ${variantTitle}` : getLineTitle(relatedLine);
        });
        const requests = [...selectedRequestNames, ...relatedRequestNames];
        const lineTotal = line.cost?.totalAmount || {};
        const unitPrice = line.merchandise?.price || {};

        return (
          <CartItem key={line.id || index}>
            <CartTitleRow>
              <div>
                <CartTitle>{getLineTitle(line)}</CartTitle>
                {getVariantTitle(line) && <CartVariant>{getVariantTitle(line)}</CartVariant>}
              </div>
              <RemoveButton type="button" onClick={() => handleRemove(line.id)}>Remove</RemoveButton>
            </CartTitleRow>

            <CartMeta>
              <MetaPill>Qty {line.quantity}</MetaPill>
              <MetaPill>{formatMoney(unitPrice.amount, unitPrice.currencyCode)}</MetaPill>
              <MetaPill>{formatMoney(lineTotal.amount, lineTotal.currencyCode)}</MetaPill>
            </CartMeta>

            {requests.length > 0 && (
              <RequestList>
                {requests.map((request, requestIndex) => (
                  <RequestPill key={`${request}-${requestIndex}`}>{request}</RequestPill>
                ))}
              </RequestList>
            )}
          </CartItem>
        );
      })}
      <CartTotal>
        <span>Subtotal</span>
        <span>{formatMoney(cost?.totalAmount?.amount, cost?.totalAmount?.currencyCode)}</span>
      </CartTotal>
    </CartBody>
  );
};

export default CartDetails;
